/**
 * The escalation queue (APP_SPEC box 6), worn in Ant Design: elements
 * awaiting a human ruling, shown with the type-level identity the
 * classifier saw. One click per row IS the ruling -- the answer
 * publishes to the worker, the flywheel learns it, and the queue
 * shrinks.
 */
import { useSearch } from '@tanstack/react-router';
import { Button, Space, Table, Typography } from 'antd';
import type { ElementFilter } from '../../../api';
import { CategorySwatch } from '../../../strata';
import {
  CATEGORIES,
  useOverrideClassification,
  usePendingClassifications,
  type PendingClassification,
} from '../queries';

export function ReviewPanel({
  projectId,
  signedBy,
}: {
  projectId: string;
  signedBy: string;
}) {
  const { data: pending = [], isLoading } = usePendingClassifications(projectId);
  const override = useOverrideClassification(projectId, signedBy);
  const filter = useSearch({ strict: false }) as ElementFilter;

  const rows = filter.ifc_class
    ? pending.filter((p) => p.ifc_class === filter.ifc_class)
    : pending;

  if (isLoading) {
    return <Typography.Text type="secondary">Loading the queue…</Typography.Text>;
  }
  if (rows.length === 0) {
    return (
      <Typography.Text type="success">
        ✓ Nothing awaits a human — every element classified confidently.
      </Typography.Text>
    );
  }

  const columns = [
    {
      title: 'Element',
      key: 'element',
      render: (item: PendingClassification) => (
        <>
          <div>{item.element_name ?? item.origin_id}</div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {item.ifc_class ?? 'unknown class'}
            {item.object_type ? ` · ${item.object_type}` : ''}
          </Typography.Text>
        </>
      ),
    },
    {
      title: 'Rule it',
      key: 'rule',
      render: (item: PendingClassification) => (
        <Space wrap>
          {CATEGORIES.map((category) => (
            <Button
              key={category}
              size="small"
              disabled={override.isPending}
              onClick={() => override.mutate({ elementId: item.element_id, category })}
            >
              <Space size={6}>
                <CategorySwatch category={category} size={8} />
                {category}
              </Space>
            </Button>
          ))}
        </Space>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        {rows.length} element{rows.length === 1 ? '' : 's'} awaiting your ruling — the
        answer teaches the flywheel, so siblings resolve with it.
      </Typography.Text>
      <Table
        rowKey="element_id"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
      />
    </Space>
  );
}
